import { useState, useRef, useEffect, Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  Bell,
  ChevronRight,
  LogOut,
  User,
  Settings,
  Plus,
  ClipboardList,
  Users,
  FileText,
  Command,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { Avatar } from '@/components/ui/Avatar';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore, type BreadcrumbItem } from '@/stores/uiStore';

// ──────────────────────────────────────────────
// Header — Top bar with breadcrumbs, search, and user menu
// ──────────────────────────────────────────────

function Breadcrumbs({ items }: { items: BreadcrumbItem[] }) {
  if (items.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm">
      {items.map((item, index) => (
        <Fragment key={index}>
          {index > 0 && (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />
          )}
          {item.href && index < items.length - 1 ? (
            <Link
              to={item.href}
              className="text-slate-500 transition-colors hover:text-slate-700"
            >
              {item.label}
            </Link>
          ) : (
            <span
              className={cn(
                index === items.length - 1
                  ? 'font-medium text-slate-900'
                  : 'text-slate-500'
              )}
            >
              {item.label}
            </span>
          )}
        </Fragment>
      ))}
    </nav>
  );
}

interface QuickAction {
  label: string;
  icon: typeof Plus;
  href?: string;
  onClick?: () => void;
}

const quickActions: QuickAction[] = [
  { label: 'Nueva orden', icon: ClipboardList, href: '/work-orders/new' },
  { label: 'Nuevo cliente', icon: Users, href: '/customers?new=true' },
  { label: 'Nuevo presupuesto', icon: FileText, href: '/estimates?new=true' },
];

function Header() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const breadcrumbs = useUIStore((s) => s.breadcrumbs);
  const setCommandPaletteOpen = useUIStore((s) => s.setCommandPaletteOpen);

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const quickActionsRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
      if (quickActionsRef.current && !quickActionsRef.current.contains(event.target as Node)) {
        setQuickActionsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut: Cmd+K
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [setCommandPaletteOpen]);

  function handleLogout() {
    logout();
    navigate('/login');
  }

  return (
    <header className="flex h-16 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 lg:px-6 dark:border-slate-800 dark:bg-slate-950">
      {/* Left: Breadcrumbs */}
      <div className="flex min-w-0 flex-1 items-center">
        <Breadcrumbs items={breadcrumbs} />
      </div>

      {/* Center: Search bar */}
      <div className="hidden md:flex md:flex-1 md:justify-center">
        <button
          type="button"
          onClick={() => setCommandPaletteOpen(true)}
          className={cn(
            'flex w-full max-w-md items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2',
            'text-sm text-slate-400 transition-colors',
            'hover:border-slate-300 hover:bg-slate-100',
            'dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600'
          )}
        >
          <Search className="h-4 w-4" />
          <span className="flex-1 text-left">Buscar...</span>
          <kbd className="hidden items-center gap-0.5 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-400 sm:inline-flex dark:border-slate-600 dark:bg-slate-800">
            <Command className="h-3 w-3" />K
          </kbd>
        </button>
      </div>

      {/* Right: Actions */}
      <div className="flex flex-1 items-center justify-end gap-1">
        {/* Mobile search */}
        <button
          type="button"
          onClick={() => setCommandPaletteOpen(true)}
          className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 md:hidden"
          aria-label="Buscar"
        >
          <Search className="h-5 w-5" />
        </button>

        {/* Quick actions */}
        <div className="relative" ref={quickActionsRef}>
          <button
            type="button"
            onClick={() => setQuickActionsOpen(!quickActionsOpen)}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors',
              'hover:bg-slate-100 hover:text-slate-600',
              quickActionsOpen && 'bg-slate-100 text-slate-600'
            )}
            aria-label="Acciones rapidas"
          >
            <Plus className="h-5 w-5" />
          </button>

          {quickActionsOpen && (
            <div className="absolute right-0 top-full z-50 mt-2 w-52 rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900">
              {quickActions.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  onClick={() => {
                    setQuickActionsOpen(false);
                    if (action.href) navigate(action.href);
                    if (action.onClick) action.onClick();
                  }}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  <action.icon className="h-4 w-4 text-slate-400" />
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Notifications */}
        <button
          type="button"
          className="relative rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          aria-label="Notificaciones"
        >
          <Bell className="h-5 w-5" />
          {/* Badge — hardcoded for now, will be driven by API */}
          <span className="absolute right-1.5 top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
            3
          </span>
        </button>

        {/* User dropdown */}
        <div className="relative ml-1" ref={userMenuRef}>
          <button
            type="button"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className={cn(
              'flex items-center gap-2 rounded-lg p-1.5 transition-colors',
              'hover:bg-slate-100',
              userMenuOpen && 'bg-slate-100'
            )}
            aria-label="Menu de usuario"
            aria-expanded={userMenuOpen}
          >
            <Avatar
              name={user?.name}
              src={user?.avatarUrl}
              size="sm"
            />
            <span className="hidden text-sm font-medium text-slate-700 lg:inline-block">
              {user?.name?.split(' ')[0]}
            </span>
          </button>

          {userMenuOpen && (
            <div className="absolute right-0 top-full z-50 mt-2 w-56 rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900">
              {/* User info */}
              <div className="border-b border-slate-100 px-3 py-2.5 dark:border-slate-800">
                <p className="text-sm font-medium text-slate-900 dark:text-white">
                  {user?.name}
                </p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>

              {/* Menu items */}
              <div className="py-1">
                <button
                  type="button"
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/settings/profile');
                  }}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  <User className="h-4 w-4 text-slate-400" />
                  Mi perfil
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate('/settings');
                  }}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  <Settings className="h-4 w-4 text-slate-400" />
                  Ajustes
                </button>
              </div>

              {/* Logout */}
              <div className="border-t border-slate-100 py-1 dark:border-slate-800">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-rose-600 transition-colors hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/30"
                >
                  <LogOut className="h-4 w-4" />
                  Cerrar sesion
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

Header.displayName = 'Header';

export { Header };
